import {
  SimulationWrapperType,
  type SimulationWrapperConfig,
} from "@/test-set/simulation-wrappers"

import { isJsonObjectValue, type JsonFieldValue } from "@/types/json"

import type {
  EditableTestCase,
  EditableTestCaseRecordLike,
  PendingChanges,
} from "../model"

function asSimulationWrapperConfig(
  value: JsonFieldValue | undefined,
): SimulationWrapperConfig | null {
  if (!isJsonObjectValue(value)) {
    return null
  }

  const preSimulation = value.pre_simulation
  const postSimulation = value.post_simulation
  if (
    (preSimulation !== undefined && !Array.isArray(preSimulation)) ||
    (postSimulation !== undefined && !Array.isArray(postSimulation))
  ) {
    return null
  }

  return value as unknown as SimulationWrapperConfig
}

export function getTestSetSimulationWrapperConfig(
  wrapperType: SimulationWrapperType | null | undefined,
  testCases: Pick<EditableTestCaseRecordLike, "simulation_metadata_input">[],
): SimulationWrapperConfig | null {
  if (wrapperType !== SimulationWrapperType.API_WRAPPER) {
    return null
  }

  for (const testCase of testCases) {
    const config = asSimulationWrapperConfig(
      testCase.simulation_metadata_input?.[wrapperType],
    )
    if (config) {
      return config
    }
  }

  return null
}

function applyApiSetup<
  T extends Pick<EditableTestCase, "simulation_metadata_input">,
>(testCase: T, config: SimulationWrapperConfig): T {
  return {
    ...testCase,
    simulation_metadata_input: {
      ...(testCase.simulation_metadata_input ?? {}),
      api: config as unknown as JsonFieldValue,
    },
  }
}

export function applyTestSetSimulationWrapperConfig({
  config,
  testCases,
}: {
  config: SimulationWrapperConfig
  testCases: EditableTestCase[]
}): EditableTestCase[] {
  return testCases.map((testCase) => applyApiSetup(testCase, config))
}

export function applyApiSetupToPendingChanges(
  pending: PendingChanges | null,
  config: SimulationWrapperConfig,
): PendingChanges | null {
  if (pending?.type === "ADD") {
    return {
      ...pending,
      data: applyTestSetSimulationWrapperConfig({
        testCases: pending.data,
        config,
      }),
    }
  }
  if (pending?.type === "EDIT") {
    return {
      ...pending,
      data: pending.data.map((patch) =>
        patch.simulation_metadata_input === undefined
          ? patch
          : applyApiSetup(patch, config),
      ),
    }
  }
  return pending
}
